/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_ESA_OFFICE } from "utils";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { API_ESA_OFFICE_UPLOAD } from "utils";
import { logoutUser } from "actions/AuthAction";

function DetailAset() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [aset, setAset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [dinasId, setDinasId] = useState(null);
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
    } else {
      logoutUser(navigate);
    }
  }, []);

  useEffect(() => {
    const fetchAset = async () => {
      try {
        const response = await axios.get(`${API_ESA_OFFICE}aset/${id}`);
        setAset(response.data);
        setLoading(false);
      } catch (err) {
        setError("Gagal memuat data aset");
        setLoading(false);
      }
    };

    fetchAset();
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleImageClick = (foto) => {
    setSelectedImage(foto);
    setOpenImageDialog(true);
  };

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox my={3}>
          <Card>
            <MDBox p={3}>
              <MDTypography>Memuat data...</MDTypography>
            </MDBox>
          </Card>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox my={3}>
          <Card>
            <MDBox p={3}>
              <MDTypography color="error">{error}</MDTypography>
            </MDBox>
          </Card>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!aset) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox my={3}>
          <Card>
            <MDBox p={3}>
              <MDTypography>Data aset tidak ditemukan</MDTypography>
            </MDBox>
          </Card>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                >
                  <MDTypography variant="h4">Detail Aset</MDTypography>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleBack}
                  >
                    <Icon>arrow_back</Icon>&nbsp;Kembali
                  </MDButton>
                </MDBox>

                <Grid container spacing={3}>
                  {/* Informasi Dasar */}
                  <Grid item xs={12} md={6}>
                    <MDBox mb={3}>
                      <MDTypography variant="h6" mb={2}>
                        Informasi Dasar
                      </MDTypography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            Kode Barang:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.kodeBarang}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            Register:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.register}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            Nama Barang:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.nama_barang}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            Merk/Type:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.merk_type}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            No. Identitas Barang:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.no_identitas_barang}
                          </MDTypography>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Grid>

                  {/* Informasi Tambahan */}
                  <Grid item xs={12} md={6}>
                    <MDBox mb={3}>
                      <MDTypography variant="h6" mb={2}>
                        Informasi Tambahan
                      </MDTypography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            Bahan:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.bahan}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            Asal Perolehan:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.asal_perolehan}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            Tahun Perolehan:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.tahun_perolehan}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            Ukuran Barang:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.ukuran_barang}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            Satuan:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.satuan}
                          </MDTypography>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Grid>

                  {/* Status dan Keterangan */}
                  <Grid item xs={12} md={6}>
                    <MDBox mb={3}>
                      <MDTypography variant="h6" mb={2}>
                        Status dan Keterangan
                      </MDTypography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            Keadaan Barang:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.keadaan_barang}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            Jumlah Barang:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.jumlah_barang}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            Harga Satuan:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.harga_satuan
                              ? `Rp ${Number(aset.harga_satuan).toLocaleString(
                                  "id-ID"
                                )}`
                              : "-"}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography variant="subtitle2">
                            Keterangan:
                          </MDTypography>
                          <MDTypography variant="body1">
                            {aset.keterangan || "-"}
                          </MDTypography>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Grid>

                  {/* Foto Aset */}
                  <Grid item xs={12} md={6}>
                    <MDBox mb={3}>
                      <MDTypography variant="h6" mb={2}>
                        Foto Aset
                      </MDTypography>
                      <Grid container spacing={2}>
                        {aset.foto &&
                          JSON.parse(aset.foto).map((foto, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                              <Card>
                                <MDBox p={2}>
                                  <img
                                    src={`${API_ESA_OFFICE_UPLOAD}${foto}`}
                                    alt={`Foto ${index + 1}`}
                                    style={{
                                      width: "100%",
                                      height: 200,
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => handleImageClick(foto)}
                                  />
                                </MDBox>
                              </Card>
                            </Grid>
                          ))}
                      </Grid>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog
        open={openImageDialog}
        onClose={handleCloseImageDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ position: "relative", p: 0 }}>
          <IconButton
            onClick={handleCloseImageDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              bgcolor: "rgba(0,0,0,0.5)",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.7)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <img
              src={`${API_ESA_OFFICE_UPLOAD}${selectedImage}`}
              alt="Foto Aset"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default DetailAset;
